import { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { BookOpen, ChevronRight, Download, Target, Clock, ListOrdered, Lightbulb, Search, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function SectionCard({ s, n, shotsAvailable }) {
  return (
    <section id={s.id} className="mb-16 scroll-mt-24" data-testid={`tutorial-section-${s.id}`}>
      <div className="flex items-start gap-3 mb-4 pb-2 border-b border-primary/20">
        <div className="w-9 h-9 flex-shrink-0 bg-primary text-primary-foreground flex items-center justify-center font-black text-sm">{n}</div>
        <div className="min-w-0">
          <h2 className="text-xl md:text-2xl font-black tracking-tight leading-tight">{s.title}</h2>
          {s.route && (
            <Link to={s.route} className="inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline mt-1" data-testid={`tutorial-route-${s.id}`}>
              <ExternalLink size={11} /> Abrir módulo: {s.route}
            </Link>
          )}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3 mb-5">
        <div className="border-l-4 border-primary bg-primary/5 p-4" data-testid={`tutorial-purpose-${s.id}`}>
          <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-primary mb-1"><Target size={12} /> ¿Para qué sirve?</div>
          <p className="text-sm leading-relaxed">{s.purpose}</p>
        </div>
        <div className="border-l-4 border-[#F29A2E] bg-[#F29A2E]/10 p-4">
          <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#b86a10] mb-1"><Clock size={12} /> ¿Cuándo usarlo?</div>
          <p className="text-sm leading-relaxed">{s.when}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2"><ListOrdered size={12} /> Paso a paso</div>
      <ol className="space-y-2 mb-5">
        {s.steps.map((p, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed">
            <span className="flex-shrink-0 w-6 h-6 border border-primary/40 text-primary font-black text-[11px] flex items-center justify-center tabular-nums">{i + 1}</span>
            <span className="text-foreground/90">{p}</span>
          </li>
        ))}
      </ol>
      {s.tips?.length > 0 && (
        <div className="border bg-muted/30 p-4 mb-5">
          <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#b86a10] mb-2"><Lightbulb size={12} /> Tips del experto</div>
          <ul className="space-y-1.5">
            {s.tips.map((p, i) => <li key={i} className="text-sm leading-relaxed flex gap-2"><span className="text-[#F29A2E] font-black">•</span><span>{p}</span></li>)}
          </ul>
        </div>
      )}
      {s.shot && shotsAvailable.includes(s.shot) && (
        <figure className="border bg-muted/30" data-testid={`tutorial-shot-${s.shot}`}>
          <img src={`${API_BASE}/api/tutorial/shots/${s.shot}`} alt={s.title} className="w-full h-auto" loading="lazy" />
          <figcaption className="text-[10px] uppercase tracking-widest text-muted-foreground p-2 border-t">Pantalla real: {s.title}</figcaption>
        </figure>
      )}
    </section>
  );
}

export default function Tutorial() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("intro");
  const [q, setQ] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { api.get("/tutorial").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const sections = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.sections;
    return data.sections.filter((s) => JSON.stringify(s).toLowerCase().includes(needle));
  }, [data, q]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/tutorial/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Manual_MFIS_Supply-Chain.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Manual PDF descargado");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDownloading(false); }
  };

  if (!data) return <div className="p-8 text-sm text-muted-foreground" data-testid="tutorial-loading">Cargando tutorial…</div>;
  const numbering = Object.fromEntries(data.sections.map((s, i) => [s.id, i + 1]));

  return (
    <div data-testid="tutorial-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 pb-6 border-b">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-2">{t("tut.subtitle")} · v{data.version}</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter">Manual de usuario</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Cada módulo explicado como lo haría un instructor: para qué sirve, cuándo usarlo, el paso a paso y los tips que evitan errores. {data.sections.length} temas con pantallas reales del sistema.</p>
        </div>
        <Button onClick={downloadPdf} disabled={downloading} className="rounded-none font-bold" data-testid="tutorial-download-pdf">
          <Download size={14} className="mr-2" /> {downloading ? "Generando PDF…" : "Descargar manual en PDF"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        <aside className="lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          <div className="relative mb-3">
            <Search size={12} className="absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en el manual…" className="rounded-none h-8 text-xs pl-7" data-testid="tutorial-search" />
          </div>
          <div className="flex items-center gap-1.5 mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground"><BookOpen size={12} /> {t("tut.toc")}</div>
          <nav className="space-y-0.5" data-testid="tutorial-toc">
            {data.groups.map((g) => {
              const items = sections.filter((s) => s.group === g.id);
              if (!items.length) return null;
              return (
                <div key={g.id} className="mb-2">
                  <div className="text-[9px] font-black uppercase tracking-[0.2em] text-primary/70 px-2 py-1">{g.name}</div>
                  {items.map((s) => (
                    <a key={s.id} href={`#${s.id}`} onClick={() => setActive(s.id)} data-testid={`tutorial-toc-${s.id}`}
                      className={`flex items-start gap-1 text-xs px-2 py-1.5 border-l-2 transition-colors ${active === s.id ? "border-primary text-primary bg-primary/5 font-bold" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
                      <ChevronRight size={10} className="mt-1 flex-shrink-0" /> <span>{numbering[s.id]}. {s.title}</span>
                    </a>
                  ))}
                </div>
              );
            })}
          </nav>
        </aside>
        <main className="min-w-0">
          {sections.length === 0 && <p className="text-sm text-muted-foreground" data-testid="tutorial-empty">Sin resultados para "{q}".</p>}
          {sections.map((s) => <SectionCard key={s.id} s={s} n={numbering[s.id]} shotsAvailable={data.shots_available || []} />)}
          <div className="mt-12 pt-6 border-t text-xs text-muted-foreground">© {new Date().getFullYear()} OOMINING - mining software · Chihuahua, México · oswaldo.cereceres@oomining.com</div>
        </main>
      </div>
    </div>
  );
}
